/**
 * Landing v3 — which store this device installs from, and opening it. Kept
 * apart from the store button (`landing-get-app.tsx`) so the page chrome can
 * read it without importing the button.
 */
import { usePostHog } from 'posthog-js/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AppStore,
  detectStoreDevice,
  type StoreDevice,
  storeUrl,
} from '@/lib/store-links';

/** This device's store (see `detectStoreDevice`), known from the first render. */
export function useStoreDevice(): StoreDevice {
  return useMemo(
    () => (typeof navigator === 'undefined' ? null : detectStoreDevice(navigator)),
    [],
  );
}

/** Where on the page a store button sits, for the analytics event. */
export type GetAppPlacement = 'floating' | 'footer' | 'top-bar';

/** Count a tap on a store button (`Homepage-Get-App-Clicked`, user
 *  2026-09-28): which store, and where on the page. */
export function useTrackGetApp() {
  const posthog = usePostHog();
  return (store: AppStore, placement: GetAppPlacement) =>
    posthog.capture('Homepage-Get-App-Clicked', {
      store,
      placement,
      pathname: window.location.pathname,
    });
}

/**
 * Open a store listing, and count it. On a phone or tablet in the same tab —
 * the store app takes over, and Back returns here; a new tab would leave an
 * empty page behind. A computer (the top bar's QR tiles) keeps a new tab.
 */
export function useOpenStore() {
  const track = useTrackGetApp();
  const { i18n } = useTranslation();
  return (store: AppStore, placement: GetAppPlacement, sameTab: boolean) => {
    track(store, placement);
    const href = storeUrl(store, (i18n.language || 'en').split('-')[0]);
    if (sameTab) window.location.assign(href);
    else window.open(href, '_blank', 'noopener,noreferrer');
  };
}
